<script>
import moment from "moment";
import axios from '@/utils/axios';
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";

export default {
  inject: ['setLoading'],
  components: { Vue3ChartJs },
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "Inventory",
      text1: "Current Stock Report",
      app_currency: '',
      filter: {
        warehouse_id: '',
        item_id: '',
        item_group_id: '',
        brand_id: '',
        stock_status: '',
        sort_by: '',
        sort_dir: '',
      },
      summary: {},
      data: [],
      warehouses: [],
      items: [],
      itemGroups: [],
      brands: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    // Status-wise breakdown from summary status_count
    statusBreakdown() {
      const sc = this.summary?.status_count || {};
      const map = {
        in: { label: 'In Stock', icon: 'ti-circle-check', color: 'success', count: sc.in || 0 },
        low_stock: { label: 'Low Stock', icon: 'ti-alert-triangle', color: 'warning', count: sc.low_stock || 0 },
        out_of_stock: { label: 'Out of Stock', icon: 'ti-circle-x', color: 'danger', count: sc.out_of_stock || 0 },
      };
      return Object.values(map).filter(b => b.count > 0);
    },

    // Item-wise available qty bar chart
    itemBarChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.item?.name || 'Unknown';
        const qty = parseFloat(row.available_qty || 0);
        map[name] = (map[name] || 0) + qty;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#4f46e5', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
      return {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Available Qty',
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderRadius: 6,
            barThickness: 40,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } }
          }
        }
      };
    },

    // Warehouse-wise qty doughnut
    warehouseDoughnutChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.warehouse?.name || 'Unknown';
        const qty = parseFloat(row.qty_on_hand || 0);
        map[name] = (map[name] || 0) + qty;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#4f46e5', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },

    // Brand-wise qty doughnut
    brandDoughnutChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.item?.brand?.name || 'Unknown';
        const qty = parseFloat(row.qty_on_hand || 0);
        map[name] = (map[name] || 0) + qty;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#10b981', '#f59e0b', '#4f46e5', '#ef4444', '#06b6d4', '#8b5cf6'];
      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },
  },

  async mounted() {
    this.app_currency = await this.$appCurrency();
    this.getDropdowns();
    this.filterReport();
  },

  methods: {

    async onBrandFilterChange() {
  this.filter.item_id = '';

  if (this.filter.brand_id) {
    try {
      const res = await axios.get(`/items/${this.filter.brand_id}/brand-wise`);
      this.items = res.data.data || [];
    } catch (e) {
      console.log('Item by brand error:', e.message);
      this.items = [];
    }
  } else {
    try {
      const res = await axios.get('/items/dropdown');
      this.items = res.data.data || [];
    } catch (e) {
      console.log('Item dropdown error:', e.message);
    }
  }

  this.filterReport();
},

    sortTable(field) {
  if (this.filter.sort_by === field) {
    this.filter.sort_dir = this.filter.sort_dir === 'asc' ? 'desc' : 'asc';
  } else {
    this.filter.sort_by = field;
    this.filter.sort_dir = 'asc';
  }
  this.filterReport();
},

    // Stock status badge for a row
    statusInfo(row) {
      const avail = parseFloat(row.available_qty || 0);
      const onHand = parseFloat(row.qty_on_hand || 0);
      const threshold = parseFloat(row.item?.low_stock_threshold || 0);
      if (avail <= 0 && onHand <= 0) {
        return { label: 'Out of Stock', class: 'bg-danger-subtle text-danger', icon: 'ti-circle-x' };
      }
      if (threshold && avail <= threshold) {
        return { label: 'Low Stock', class: 'bg-warning-subtle text-warning', icon: 'ti-alert-triangle' };
      }
      return { label: 'In Stock', class: 'bg-success-subtle text-success', icon: 'ti-circle-check' };
    },

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv' };
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
        if (this.filter.item_group_id) payload.item_group_id = this.filter.item_group_id;
        if (this.filter.brand_id) payload.brand_id = this.filter.brand_id;
        if (this.filter.stock_status) payload.stock_status = this.filter.stock_status;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/current-stock?${query_string}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `current-stock-report-${moment().format('YYYY-MM-DD')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport(page = 1) {
      this.errors = {};
      this.setLoading(true);
      try {
        const payload = { page };
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
        if (this.filter.item_group_id) payload.item_group_id = this.filter.item_group_id;
        if (this.filter.brand_id) payload.brand_id = this.filter.brand_id;
        if (this.filter.stock_status) payload.stock_status = this.filter.stock_status;
        if (this.filter.sort_by) payload.sort_by = this.filter.sort_by;
        if (this.filter.sort_dir) payload.sort_dir = this.filter.sort_dir;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/current-stock?${query_string}`);
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links
        };
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
        try {
            const [warehouseRes, itemRes, groupRes, brandRes] = await Promise.all([
            axios.get('/inventory/warehouses/dropdown'),
            axios.get('/items/dropdown'),
            axios.get('/item-groups'),
            axios.get('/brands'),
            ]);
            this.warehouses = warehouseRes.data.data || [];
            this.items = itemRes.data.data || [];
            // ⬇️ These two — check actual response structure
            this.itemGroups = Array.isArray(groupRes.data.data) ? groupRes.data.data : (groupRes.data.data?.data || []);
            this.brands = Array.isArray(brandRes.data.data) ? brandRes.data.data : (brandRes.data.data?.data || []);
        } catch (error) {
            console.log('Dropdown load error:', error.message);
        }
        },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return this.filter.warehouse_id || this.filter.item_id || this.filter.item_group_id || this.filter.brand_id || this.filter.stock_status;
    },

    clearFilter() {
      this.filter.warehouse_id = '';
      this.filter.item_id = '';
      this.filter.item_group_id = '';
      this.filter.brand_id = '';
      this.filter.stock_status = '';
      this.getDropdowns();
      this.filterReport();
    },

    formatQty(val) {
      const num = parseFloat(val || 0);
      return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_skus != null">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total SKUs</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_skus }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Warehouses</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_warehouses }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Qty on Hand</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatQty(summary.total_qty_hand) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Reserved</span>
              <span class="fw-bold text-warning" style="font-size:20px;">{{ formatQty(summary.total_reserved) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Available</span>
              <span class="fw-bold text-success" style="font-size:20px;">{{ formatQty(summary.total_available) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Stock Health</span>
              <span class="fw-bold text-primary" style="font-size:20px;">{{ summary.stock_health_pct }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Status-wise Breakdown Cards -->
      <div class="row g-3 mb-3" v-if="statusBreakdown.length > 1">
        <div class="col" v-for="src in statusBreakdown" :key="src.label">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="d-flex align-items-center">
                  <i class="ti me-2" :class="[src.icon, 'text-' + src.color]" style="font-size:18px;"></i>
                  <span class="fw-semibold" style="font-size:13px;">{{ src.label }}</span>
                </span>
                <span class="badge rounded-pill" :class="'bg-' + src.color + '-subtle text-' + src.color">{{ src.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row g-3 mb-3" v-if="data && data.length">
        <div class="col-md-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-chart-bar me-1 text-primary"></i>Item-wise Available Qty</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="itemBarChart" :key="'bar-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-building-warehouse me-1 text-primary"></i>By Warehouse</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="warehouseDoughnutChart" :key="'wh-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-tag me-1 text-primary"></i>By Brand</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="brandDoughnutChart" :key="'br-'+data.length" />
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.warehouse_id"
                :options="warehouses"
                label="name"
                :reduce="w => w.id"
                placeholder="Warehouse"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 170px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.item_group_id"
                :options="itemGroups"
                label="name"
                :reduce="g => g.id"
                placeholder="Product Group"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 170px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.brand_id"
                :options="brands"
                label="name"
                :reduce="b => b.id"
                placeholder="Brand"
                :clearable="true"
                @update:modelValue="onBrandFilterChange"
                style="min-width: 170px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.item_id"
                :options="items"
                label="name"
                :reduce="i => i.id"
                placeholder="Item"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 170px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.stock_status"
                :options="[
                { label: 'In Stock', value: 'in' },
                { label: 'Low Stock', value: 'low' },
                { label: 'Out of Stock', value: 'out' },
                ]"
                label="label"
                :reduce="s => s.value"
                placeholder="Stock Status"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 170px;"
              ></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item</th>
                    <th>Group</th>
                    <th>Brand</th>
                    <th>Warehouse</th>
                    <th @click="sortTable('qty_on_hand')" style="cursor:pointer;">
                    On Hand
                    <i v-if="filter.sort_by === 'qty_on_hand'"
                        :class="filter.sort_dir === 'asc' ? 'ti ti-arrow-up' : 'ti ti-arrow-down'"
                        style="font-size:12px;"></i>
                    <i v-else class="ti ti-arrows-sort text-muted" style="font-size:12px; opacity:0.4;"></i>
                    </th>
                    <th>Reserved</th>
                    <th>On Order</th>
                    <th>Available</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td>
                      <strong class="text-dark">{{ row.item?.name }}</strong>
                      <small class="text-muted d-block" style="font-size:11px;">{{ row.item?.code }}</small>
                    </td>
                    <td>
                      <span v-if="row.item?.item_group">{{ row.item.item_group.name }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="row.item?.brand">{{ row.item.brand.name }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span class="badge bg-light text-primary border rounded-pill">{{ row.warehouse?.name || '-' }}</span>
                    </td>
                    <td><strong>{{ formatQty(row.qty_on_hand) }}</strong></td>
                    <td><span class="text-warning">{{ formatQty(row.reserved) }}</span></td>
                    <td>{{ formatQty(row.on_order) }}</td>
                    <td><strong class="text-success">{{ formatQty(row.available_qty) }}</strong></td>
                    <td>
                      <span class="badge rounded-pill" :class="statusInfo(row).class">
                        <i :class="statusInfo(row).icon" class="me-1" style="font-size:11px;"></i>{{ statusInfo(row).label }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <!-- Pagination -->
              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.table-responsive {
  overflow-y: auto;
}
</style>